<template>
  <lock-loader v-if="loading" text="Carregando..." asset="logo-alt.svg" />
  <div v-else class="tenant-info-wrapper">
    <div class="tenant-info-box">
      <TenantHeader />

      <p class="tenant-info-subtitle">
        Resumo da solicitação de empréstimo:
      </p>

      <div class="summary-info only-on-large">
        <div class="tenant">
          <p class="info-title">Locatário</p>
          <p class="info-value">{{ proposal.lesseeName }}</p>
        </div>
        <div class="address">
          <p class="info-title">Endereço do imóvel</p>
          <p class="info-value">
            {{ proposal.addressStreet }}, {{ proposal.addressNumber }}
            <br/>
            {{ proposal.zipCode | zipCodeFilter }}
            <br/>
            {{ proposal.city }}/{{ proposal.state }}
          </p>
        </div>
        <div class="cpf">
          <p class="info-title">CPF</p>
          <p class="info-value">{{ proposal.lesseeCpf | cpfFilter }}</p>
        </div>
      </div>

      <div class="condition-title only-on-large">
        <h3 class="gray-700">Condições de pagamento</h3>
        <p
          @click="() => $router.push('/locatario/opcoes')"
          class="body-regular primary"
        >
          Alterar
        </p>
      </div>

      <div class="condition-row">
        <p class="body-regular">
          Entrada de {{ selectedDownPaymentOption.downPaymentPercentage }}%
        </p>
        <p class="body-bold">{{ selectedDownPaymentOption.downPaymentValue | moneyFilter }}</p>
      </div>

      <Divisor class="condition-divisor" />

      <div class="condition-row">
        <p class="body-regular">Parcelas</p>
        <p class="body-bold">
          {{ selectedDownPaymentOption.numberOfInstallments }}
          vezes de {{ selectedDownPaymentOption.paymentInstallments | moneyFilter }}
        </p>
      </div>

      <Divisor class="condition-divisor" />

      <div class="condition-row">
        <p class="body-regular">Valor total</p>
        <div>
          <p class="body-bold">{{ selectedDownPaymentOption.totalAmount | moneyFilter }}</p>
          <p class="extra-condition-info">
            Juros de {{ selectedDownPaymentOption.interestRatePM | interestRateFilter }} a.m. + IOF.
            <br/>
            CET máximo de {{ selectedDownPaymentOption.cetPA | interestRateFilter }} a.a.
          </p>
        </div>
      </div>

      <Checkbox
        class="checkbox-agree"
        @input="handleCheckboxClick"
        :errorMessage="agreeErrorMessage"
        :checked="agreeCheckbox"
      >
        <p>
          Ao finalizar, declaro assinada a
          <Link text="Cédula de Crédito Bancário" :url="ccbHref" newTab />
        </p>
      </CheckBox>

      <div class="finish-btn">
        <Button class="primary" @click="handleFinish">
          Finalizar
        </Button>
      </div>

    </div>
  </div>
</template>

<script>
import querystring from 'querystring';
import { mapState } from 'vuex';
import axios from 'axios';
import Divisor from '@/components/common/Divisor.vue';
import Checkbox from '@/components/common/Checkbox.vue';
import Link from '@/components/common/Link.vue';
import Button from '@/components/ui/AppButton.vue';
import stringMixin from '@/mixins/string';
import LockLoader from '@/components/common/LockLoader.vue';
import Logger from '@/lib/logger';
import TenantHeader from '../components/TenantHeader.vue';
import globals from '../../../globals';

export default {
  name: 'tenantSummary',
  metaInfo: {
    title: 'Sumário',
  },
  components: {
    TenantHeader,
    Divisor,
    Checkbox,
    Link,
    Button,
    LockLoader,
  },
  mixins: [stringMixin],
  data() {
    return {
      loading: false,
      agreeErrorMessage: null,
      agreeCheckbox: false,
    };
  },
  computed: {
    ...mapState('tenant', ['proposal', 'selectedDownPaymentOption']),
    ccbHref() {
      return `${globals().apiUrl}/public/ccb/${this.selectedDownPaymentOption._id}`;
    },
  },
  methods: {
    handleCheckboxClick(newValue) {
      this.agreeCheckbox = newValue;
      if (this.agreeErrorMessage) this.agreeErrorMessage = null;
    },
    async handleFinish() {
      if (!this.agreeCheckbox) {
        this.agreeErrorMessage = 'Você deve declarar a assinatura';
        return;
      }
      this.loading = true;
      const optionId = this.selectedDownPaymentOption._id;
      try {
        const query = querystring.stringify({ optionId });
        const { data } = await axios.get(`${this.proposal.confirmUrl}?${query}`);
        this.$router.replace({
          name: 'onboarding-main',
          query: {
            transactionId: data.transactionId,
            onboardingToken: data.onboardingToken,
            proposalId: this.proposal._id,
          },
        });
      } catch (err) {
        Logger.error(err);
        this.$router.replace({ name: 'checkout-error' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .tenant-info-wrapper {
    background-color: var(--gray-100);
  }

  .tenant-info-box {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    max-width: 608px;
    margin: 0 auto;
    padding: 24px;
    height: 100vh;
    background-color: var(--white);
  }

  .tenant-info-header {
    margin-bottom: 16px;

    img {
      width: 150px;
      height: 32px;
    }
  }

  .tenant-info-subtitle {
    @extend .body-regular;
    color: var(--gray-500);
  }

  .summary-info {
    margin: 16px 0 36px 0;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 20px;
    grid-template-areas:
      "tenant tenant . address address ."
      "cpf cpf . address address .";
  }

  .info-title {
    @extend .body-bold;
    color: var(--gray-700);
    margin-bottom: 4px;
  }

  .info-value {
    @extend .body-regular;
    color: var(--gray-700);
  }

  .tenant {
    grid-area: tenant;
  }

  .address {
    grid-area: address;
  }

  .cpf {
    grid-area: cpf;
  }

  .condition-title {
    display: flex;
    justify-content: space-between;
    margin-bottom: 24px;

    p {
      cursor: pointer;

      &:hover {
        color: var(--primary-dark);
      }
    }
  }

  .condition-row {
    text-align: right;
    display: flex;
    justify-content: space-between;
    color: var(--gray-700);
  }

  .condition-divisor {
    margin: 16px 0;
  }

  .extra-condition-info {
    @extend .small-regular;
    color: var(--gray-500);
  }

  .checkbox-agree {
    margin: 24px 0 22px 0;
  }

  @media (max-width: $breakpoint-large) {
    .tenant-info-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .tenant-info-subtitle {
      margin-bottom: 29px;
    }

    .finish-btn {
      position: fixed;
      bottom: 0;
      left: 0;
      padding: 24px;
      width: 100%;

      button {
        height: 48px;
        width: calc(100% - 48px);
      }
    }
  }

</style>
